import { types, getParent, flow, onSnapshot } from 'mobx-state-tree';
import {
  keys,
  when,
  // when
} from 'mobx';
import qs from 'qs';
import _ from 'lodash';
import { getOr } from 'lodash/fp';
import { AccountsBarListings, CheckedOrders, BulkUpdateResponse } from '../common';
import DashboardData from './DashboardData';
import FilterValues from './FilterValues';
import Actions from './Actions';
import Editable from './Editable';
// import { filterKeys } from '../../constants/listings/filter-keys';
import { monitoringStatusBool } from '../../constants/listings/set-monitoring-status-list';
import { getInitialFilterValues, getMappedParams } from './utils';
import {
  StoreCompositionData,
  Item,
  SingleListingsItemData,
  EditableCells,
} from '../../models/ListingsPage';

const statusId = {
  active: 0,
  ended: 1,
  outOfStock: 2,
  removed: 3,
};

const ListingsPageStore = types.compose(
  types
    .model('ListingsPageStore', {
      accountsBar: types.optional(AccountsBarListings, {}),
      actions: types.optional(Actions, {}),
      dashboardData: types.optional(DashboardData, {}),
      storeCompositionData: types.optional(types.array(StoreCompositionData), []),
      tableData: types.optional(types.array(Item), []),
      isAdvancedBarOpen: false,
      isActionBarOpen: false,
      tableFilter: '',
      sortDescending: types.maybe(types.boolean),
      sortColumn: types.maybe(types.number),
      checkedOrders: types.optional(types.map(types.boolean), {}),
      filterValues: types.optional(FilterValues, getInitialFilterValues()),
      page: 1,
      totalResults: types.optional(types.number, 0),
      totalResultsWithVariants: types.optional(types.number, 0),
      singleItemData: types.optional(types.map(SingleListingsItemData), {}),
      tableDataLoaded: true,
      isMultiResult: types.optional(types.boolean, false),
      showNavButtons: true,
      endedCheckedListings: 0,
      allPagesChecked: false,
      emptyEditableState: types.maybe(Editable),
      emptyEditableCellsState: types.maybe(EditableCells),
      limit: 100,
      tableDataParamsClone: types.optional(types.frozen, {}),
      isUpdated: false,
      bulkUpdateDetails: types.optional(BulkUpdateResponse,{}),
      bulkUpdateDetailsModalOpen:false,
      isSpecificUpdating:false,
    })
    .views(self => ({
      // @todo remove all usages
      get filteredTableData() {
        return self.tableData;
      },
      get transport5007() {
        return getParent(self).transport5007;
      },
      get accountCode() {
        return getParent(self).sessionStore.accountCode;
      },
      get normalizedDashboardData() {
        const data = { ...self.dashboardData };
        delete data.outOfStockDaysNumberValue;
        return data;
      },
      get headers() {
        return { 'Account-Id': self.accountCode };
      },
      get tableDataAsObject() {
        return _.keyBy(self.tableData, o => o.id);
      },
      get days() {
        return self.dashboardData.outOfStockDaysNumberValue;
      },
      get templateList() {
        return getOr([], 'settingsStore.listing.templateList', getParent(self));
      },
      get sources() {
        return getOr([], 'settingsStore.markets.sources', getParent(self));
      },
      get checkedOrdersArray() {
        return keys(self.checkedOrders);
      },
      get selectedItemsCount() {
        return self.checkedOrders.size;
      },
      get resultsItemsCount() {
        return self.tableData.length;
      },
      get filteredItemsCount() {
        if (self.tableFilter !== '') {
          return self.resultsItemsCount;
        }
        return 0;
      },
      get start() {
        return (self.page - 1) * self.limit;
      },
      get browserHistory() {
        return getParent(self).routingStore.browserHistory;
      },
      get currentLanguage() {
        return getParent(self).settingsStore.userProfile.languageId || 'en';
      },
      get itemsVariationsReady() {
        if (self.tableData.some(item => !item.variationsReady)) {
          return false;
        }
        return true;
      },
      get showToast() {
        return getParent(self).toastsStore.showToast;
      },
      get enums() {
        return getParent(self).enumsStore.getEnumsList;
      },
      get tableDataParams() {
        let params = {
          destinationMarketplace:
            self.accountsBar.selectedDestinationMarketplace?.id ||
            self.accountsBar.defaultDestinationMarketplace.id,
          sellerAccountId: getOr('', 'accountsBar.accountsQueryString', self),
          start: self.start,
          limit: self.limit,
        };
        if (self.tableFilter) {
          params.searchText = self.tableFilter;
        }
        if (self.sortDescending !== null && self.sortColumn !== null) {
          params.sortDescending = self.sortDescending;
          params.sortColumn = self.sortColumn;
        }
        params = { ...params, ...getMappedParams(self.filterValues) };
        return params;
      },
      get isUp() {
        const val = getParent(self).sessionStore.endpointsAvailability.get('seller-listing');
        if (typeof val === 'undefined') {
          return true;
        }
        return val;
      },
    }))
    .volatile(self => ({
      BASE_URL: 'seller-listing/v1',
      getTemplateName(id) {
        const templateObj = _.keyBy(self.templateList, t => t.id);
        return getOr('', 'name', templateObj[id]);
      },
      getSourceName(id) {
        const sourceObj = _.keyBy(self.sources, s => s.id);
        return getOr('', 'name', sourceObj[id]);
      },
      ifItemChecked(id) {
        return self.checkedOrders.get(id) !== undefined;
      },
      getSingleItem(code) {
        return self.singleItemData.get(code);
      },
    }))
    .actions(self => ({
      afterCreate() {
        onSnapshot(self.accountsBar, () => {
          if (window.location.pathname.indexOf('listings') !== -1) {
            self.fetchInitialData();
          }
        });
        self.emptyEditableState = Editable.create({});
      },
      updateSpecific: flow(function* updateSpecific(specificId, newValue,itemId) {
        self.isSpecificUpdating=true;
        const body = {
          id: specificId,
          propsToUpdate: newValue,
        };
        try {
          yield self.transport5007.put(
            `seller-listing/v1/Items/${itemId}/update-specification`,
            body,
            null,
            {},
          );

          self.isSpecificUpdating=false;
          return true;
        } catch (error) {
          self.isSpecificUpdating=false;
          return error;
        }
      }),
      addItemSpecific: flow(function* addItemSpecific(params) {
        try {
          const { data } = yield self.transport5007.post(
            `seller-listing/v1/Items/item-specific`,
            params,
          );
          self.showToast({
            type: self.enums.TOAST_TYPES.INFO,
            message: 'Specific Added successfully!',
          });
          self.getSingleItem(params.itemId).addSpecific(data)
          return data;
        } catch (error) {
          self.showToast({
            type: self.enums.TOAST_TYPES.WARNING,
            message: 'Adding Specific failed',
          });
          return null;
        }
      }),
      deleteItemSpecific: flow(function* deleteItemSpecific(itemSpecificId,itemId) {
        try {
          const { data } = yield self.transport5007.post(
            `seller-listing/v1/Items/remove-item-specific`,
            { itemSpecificId, itemId},
          );
          yield self.fetchSingleItemData(itemId);
          self.showToast({
            type: self.enums.TOAST_TYPES.INFO,
            message: 'Delete successful!',
          });
          return data;
        } catch (error) {
          self.showToast({
            type: self.enums.TOAST_TYPES.WARNING,
            message: 'Delete failed',
          });
          return Promise.reject(error);
        }
      }),
      fetchInitialData() {
        when(
          () => self.accountsBar.toJSON().selectedDestinationMarketplace,
          () => self.fetchAllData(),
        );
        self.emptyEditableState = Editable.create({});
      },
      fetchAllData() {
        if (!self.accountsBar.selectedSellerAccounts.every(o => !!o)) {
          self.accountsBar.init();
          return;
        }

        if (!self.accountsBar.toJSON().selectedSellerAccounts?.length) {
          self.unmountStore();
          return;
        }

        self.fetchDashboardData();
        // self.fetchStoreCompositionData();
        self.fetchTableData();
      },
      fetchDashboardData: flow(function* fetchDashboardData() {
        const URL = `${self.BASE_URL}/dashboard`;
        const params = {
          destinationMarketplace:
            self?.accountsBar?.selectedDestinationMarketplace.id ||
            self?.accountsBar?.defaultDestinationMarketplace.id,
          sellerAccountId: self?.accountsBar?.accountsQueryString ?? '',
        };
        const stringParams = qs.stringify(params, { arrayFormat: 'repeat', encode: false });
        try {
          const { data } = yield self.transport5007.fetch(
            `${URL}?${stringParams}`,
            {},
            self.headers,
          );
          self.dashboardData = data;
        } catch (err) {
          console.error('dashboard data error', err);
        }
      }),
      fetchStoreCompositionData: flow(function* fetchStoreCompositionData() {
        const URL = `${self.BASE_URL}/store-composition`;
        const params = {
          destinationMarketplace:
            self.accountsBar.selectedDestinationMarketplace?.id ||
            self.accountsBar.defaultDestinationMarketplace.id,
        };
        try {
          const { data } = yield self.transport5007.fetch(URL, params, self.headers);
          self.storeCompositionData = data;
        } catch (err) {
          console.error('store composition error', err);
        }
      }),
      fetchTableData: flow(function* fetchTableData() {
        const URL = `${self.BASE_URL}/Items`;
        const stringParams = qs.stringify(self.tableDataParams, {
          arrayFormat: 'repeat',
          encode: false,
        });
        self.tableDataParamsClone = Object.assign({}, self.tableDataParams);
        try {
          self.tableDataLoaded = false;
          // self.tableData = [];
          const { data } = yield self.transport5007.fetch(
            `${URL}?${stringParams}`,
            {},
            self.headers,
          );
          self.tableData = data.data ? data.data : data;
          self.totalResults = data.totalResults || 0;
          self.totalResultsWithVariants = data.totalResultsWithVariants || 0;
        } catch (error) {
          console.error('Table data fetch error', error);
        } finally {
          self.tableDataLoaded = true;
        }
      }),
      fetchAllCheckedItems() {
        if (self.singleItemData.size !== self.checkedOrders.size) {
          self.checkedOrdersArray.forEach(o => {
            if (self.singleItemData.get(o) === undefined) {
              self.fetchSingleItemData(o);
            }
          });
        }
      },
      fetchNextPage: flow(function* fetchNextPage(nextPage) {
        self.setPage(nextPage);
        if (self.allPagesChecked) {
          yield self.fetchTableData();
          self.toggleCheckedAllOrders(true);
        } else {
          yield self.fetchTableData();
        }
      }),
      fetchSingleItemData: flow(function* fetchSingleItemData(code) {
        try {
          const item = yield self.getSingleItemData(code);
          return item;
        } catch (err) {
          console.log(err);
          return false;
        }
      }),
      getSingleItemData: flow(function* getSingleItemData(code) {
        try {
          const url = `${self.BASE_URL}/Items/${code}`;
          const { data } = yield self.transport5007.fetch(url, {}, {});
          self.singleItemData.set(code, data);
          return self.singleItemData.get(code);
        } catch (err) {
          self.showToast({
            type: self.enums.TOAST_TYPES.WARNING,
            message: 'Item not found',
          });
          console.log(err);
          return false;
        }
      }),
      switchItem(currentCode, nextItemArrIndex) {
        const currentItemIndex = self.checkedOrdersArray.findIndex(code => code === currentCode);
        const nextItemId = self.checkedOrdersArray[currentItemIndex + nextItemArrIndex];
        const path = `/listings/single-item/${nextItemId}`;
        self.browserHistory.push(path);
      },
      executeBulkUpdate: flow(function* executeBulkUpdate() {
        try {
          const URL = `${self.BASE_URL}/Items/bulk-update`;
          const fullPayload = {
            ...self.actions.payload,
          };
          if (self.allPagesChecked) {
            const { start, limit, ...tdp } = self.tableDataParamsClone;
            fullPayload.tableDataParams = {
              ...tdp,
              sellerAccountId: getOr([], 'accountsBar.selectedSellerAccounts', self).map(
                ({ id }) => id,
              ),
            };
            fullPayload.allPagesChecked = true;
          } else {
            fullPayload.itemIds = self.checkedOrdersArray;
          }
          self.showToast({
            type: self.enums.TOAST_TYPES.INFO,
            message: 'The item update is pending',
          });
          const {data}=yield self.transport5007.post(URL, fullPayload, {}, self.headers);
          self.isUpdated = true;
          self.bulkUpdateDetails=data;

          if(!data?.allFailed && data?.invalidSellerAccounts?.length === 0){
            self.bulkUpdateDetailsModalOpen=false;
          }else{
            self.bulkUpdateDetailsModalOpen=true;
          }
          // self.actions.clear();
        } catch (err) {
          console.log(err);
          self.bulkUpdateDetailsModalOpen=true;
          self.showToast({
            type: self.enums.TOAST_TYPES.WARNING,
            message: 'There was an error updating the listings',
          });
        }
      }),
      updateSingleItem: flow(function* updateSingleItem(item) {
        const changedFields = self.getItemChangedFields(item);
        const data = [
          ...self.getMappedItemValuesToUpdate({
            ...changedFields,
            status: statusId[changedFields.status],
          }),
          ...item.removedLabels,
          ...item.addedLabels,
        ];
        const { updateCompleted, itemModel } = yield self.sendUpdateReqForSingleItem(data, item.id);
        if (updateCompleted) {
          self.singleItemData.set(item.id, itemModel);
          self.resetEditableCells();
        }
        return self.singleItemData.get(item.id);
      }),
      updateAsinSingleItem: flow(function* updateSingleItem(item) {
        const asinChanged = { sourceItemCode: item.sourceItemCode };
        const data = [...self.getMappedItemValuesToUpdate({ ...asinChanged })];
        const { isOk, updatedItem } = yield self.sendUpdateReqForSingleItem(data, item.id);
        if (isOk) {
          self.singleItemData.set(item.id, updatedItem);
        }
        return self.singleItemData.get(item.id);
      }),
      getItemChangedFields(item) {
        const sourceItem = self.singleItemData.get(item.id);
        const editableFields = {
          title: item.title,
          templateId: item.templateId,
          status: item.status,
          quantity: item.quantity,
          monitoringStatus: monitoringStatusBool[item.monitoringStatus],
        };
        const editablePricingStrategyFields = {
          salePrice: item.pricingStrategy.salePrice,
          strategyBreakEven: item.pricingStrategy.strategyBreakEven,
          strategyInPercents: item.pricingStrategy.strategyInPercents,
          strategyProfit: item.pricingStrategy.strategyProfit,
          profit: item.pricingStrategy.profit,
          profitInPercents: item.pricingStrategy.profitInPercents,
        };
        Object.keys(editableFields).forEach(key => {
          if (sourceItem[key] === editableFields[key]) {
            delete editableFields[key];
          }
        });
        Object.keys(editablePricingStrategyFields).forEach(key => {
          if (sourceItem.pricingStrategy[key] === editablePricingStrategyFields[key]) {
            delete editablePricingStrategyFields[key];
          }
        });

        if (
          editablePricingStrategyFields.profit !== undefined ||
          editablePricingStrategyFields.profitInPercents !== undefined
        ) {
          if (item.pricingStrategy.strategyInPercents) {
            editablePricingStrategyFields.strategyProfit =
              editablePricingStrategyFields.profitInPercents;
            delete editablePricingStrategyFields.profitInPercents;
          } else {
            editablePricingStrategyFields.strategyProfit = editablePricingStrategyFields.profit;
            delete editablePricingStrategyFields.profit;
          }
        }
        return { ...editableFields, ...editablePricingStrategyFields };
      },
      toggleAdvancedBar(status) {
        if (typeof status === 'boolean') {
          self.isAdvancedBarOpen = status;
        } else {
          self.isAdvancedBarOpen = !self.isAdvancedBarOpen;
        }
      },
      setEndedCheckedListings(val) {
        self.endedCheckedListings = val
          ? self.endedCheckedListings + 1
          : self.endedCheckedListings - 1;
      },
      setBulkUpdateDetailsModalOpen(val){
        self.bulkUpdateDetailsModalOpen=val;
      },
      setLimit(val) {
        if (typeof val !== 'number') {
          return;
        }
        self.limit = val;
      },
      setIsUpdated(bool) {
        self.isUpdated = bool;
      },
      resetEndedCheckedListings() {
        self.endedCheckedListings = 0;
      },
      setMultiResult(val) {
        self.isMultiResult = val;
      },
      toggleActionBar() {
        self.isActionBarOpen = !self.isActionBarOpen;
      },
      setSorting(column, direction) {
        self.sortColumn = column !== undefined ? column : null;
        self.sortDescending = direction !== undefined ? direction : null;
      },
      setTableFilter(nextFilter) {
        self.tableFilter = nextFilter.target ? nextFilter.target.value : nextFilter;
      },
      setPage(nextPage) {
        self.page = nextPage;
      },
      resetPage() {
        self.setPage(1);
      },
      clearFilters() {
        self.tableFilter = '';
        self.sortDescending = null;
        self.sortColumn = null;
        self.toggleAdvancedBar(false);
      },
      handleReturn() {
        self.browserHistory.push('/listings');
      },
      unmountStore() {
        self.tableFilter = '';
        self.resetPage();
        self.toggleAdvancedBar(false);
        self.checkedOrders.clear();
        self.tableData = [];
        self.dashboardData = {};
        self.storeCompositionData = [];
        self.isAdvancedBarOpen = false;
        self.isActionBarOpen = false;
        self.filterValues = getInitialFilterValues();
        self.totalResultsWithVariants = 0;
        self.totalResults = 0;
      },
      setShowNavButtons(val) {
        self.showNavButtons = !!val;
      },
      setAllPagesChecked(val) {
        self.allPagesChecked = !!val;
      },
    })),
  CheckedOrders,
  EditableCells,
  Editable,
);

export default ListingsPageStore;
